import { Service, inject } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';

import { Observable, from, switchMap, timeout, catchError, throwError, take } from 'rxjs';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { AuthService } from 'src/app/services/auth.service';
import { FileUploadContext } from 'src/app/models/file-upload-context';
import { GENERATE_PRESIGNED_URL_UPLOAD, GENERATE_PRESIGNED_URL_IMPORT } from 'src/app/graphql/queries/s3.queries';
import { resolveContentType } from 'src/app/models/file-type.constants';
import { isTreoEnvironment } from 'src/app/utils/environment.utils';
import { GraphQLAnalyzerPutPresignedUrlImportResponseOutput } from 'src/app/graphql/models/output/analyzer-put-presigned-url-import-response.output.model';
import { environment } from 'src/environments/environment';

interface PresignedUrlResponse {
	analyzerGeneratePutPresignedUrlFileUpload: {
		url: string;
	};
}

interface PresignedUrlImportResponse {
	analyzerGeneratePutPresignedUrlImport: GraphQLAnalyzerPutPresignedUrlImportResponseOutput;
}

/**
 * Service for uploading files with progress tracking
 * Supports both hierarchy uploads and test result imports
 */
@Service()
export class FileUploadService {
	private apolloClientService = inject(ApolloClientService);

	private environmentService = inject(EnvironmentService);

	private authService = inject(AuthService);

	private httpClient = inject(HttpClient);

	// Enable verbose logging in non-production environments
	private isDev = !environment.IsProduction;

	/**
	 * Formats HTTP errors into user-friendly messages without exposing sensitive URLs
	 * @param error The HTTP error to format
	 * @returns User-friendly error message
	 */
	private formatUploadError(error: any): string {
		if (error.name === 'TimeoutError') {
			return 'Upload timed out after 2 minutes. Please try again or check your connection.';
		}

		if (error.status === 0) {
			return 'Network error. Please check your internet connection. If using VPN, try turning it off.';
		} else if (error.status === 403) {
			return 'Upload not authorized. The upload link may have expired. Please refresh and try again.';
		} else if (error.status === 413) {
			return 'File is too large. Please upload a smaller file.';
		} else if (error.status >= 500) {
			return 'Storage service error. Please try again later.';
		} else if (error.status >= 400) {
			return `Upload failed (Error ${error.status}). Please try again.`;
		}

		return 'Failed to upload file to storage.';
	}

	/**
	 * Uploads a file to the specified hierarchy level with progress tracking
	 * @param file The file to upload
	 * @param context The upload context specifying the hierarchy level
	 * @returns Observable of HTTP events including upload progress
	 */
	public uploadFile$(file: File, context: FileUploadContext): Observable<HttpEvent<any>> {
		if (this.isDev) {
			console.log('[FileUploadService] Uploading file:', file.name, 'to context:', context);
		}

		// Step 1: Get presigned URL from GraphQL
		return from(
			this.apolloClientService.apolloClient$.pipe(
				switchMap(client => {
					const environment = this.environmentService.getCurrentEnvironment();

					const variables = {
						analyzerEnvironment: environment,
						fileName: context.fileName,
						fileType: context.fileType,
						uid: context.uid,
						analyzerSubTestSuiteUid: context.analyzerSubTestSuiteUid || null,
						analyzerTestCaseUid: context.analyzerTestCaseUid || null,
					};

					if (this.isDev) {
						console.log('[FileUploadService] Query variables:', variables);
					}

					return client.query<PresignedUrlResponse>({
						query: GENERATE_PRESIGNED_URL_UPLOAD,
						variables: variables,
						fetchPolicy: 'no-cache',
					});
				}),
				// apolloClient$ is a long-lived shareReplay stream that never completes,
				// so this inner pipe would stay open (silent) after the query emits.
				// take(1) completes it on the first result, which disarms the timeout
				// below on completion — without it, timeout() fires ~30s after the
				// (already successful) query and surfaces a spurious TimeoutError.
				take(1),
				// Guard only the presigned-URL request. Placed here (inside the query
				// pipe, before the S3 upload) so it does not also time the upload — the
				// upload has its own timeout(120000) below.
				timeout(30000),
			),
		).pipe(
			switchMap(result => {
				if (this.isDev) {
					console.log('[FileUploadService] Query result:', result);
				}

				if (result.error) {
					console.error('[FileUploadService] GraphQL error:', result.error);
					throw new Error(`GraphQL error: ${result.error.message}`);
				}

				if (!result.data) {
					console.error('[FileUploadService] No data in result:', result);
					throw new Error('No data returned from presigned URL query');
				}

				const presignedUrl = result.data.analyzerGeneratePutPresignedUrlFileUpload.url;
				if (this.isDev) {
					console.log('[FileUploadService] Got presigned URL, uploading file...');
				}

				// Step 2: Upload file to S3 using presigned URL with progress tracking.
				// Derive Content-Type from the filename so files are stored with a type that
				// lets them render in-browser when viewed (browser File.type is unreliable,
				// e.g. .log comes through empty and would otherwise become octet-stream).
				return this.httpClient
					.put(presignedUrl, file, {
						headers: {
							'Content-Type': resolveContentType(file.name, file.type),
						},
						reportProgress: true,
						observe: 'events',
					})
					.pipe(
						timeout(120000), // 2 minute timeout for large files
						catchError((error: any) => {
							console.error('[FileUploadService] S3 upload error:', error);
							const userMessage = this.formatUploadError(error);
							return throwError(() => new Error(userMessage));
						}),
					);
			}),
			catchError((error: any) => {
				console.error('[FileUploadService] Upload error:', error);
				if (error.name === 'TimeoutError') {
					return throwError(() => new Error('Request timed out. Please check your connection and try again.'));
				}
				// If error is already formatted with a message, pass it through
				if (error instanceof Error && error.message) {
					return throwError(() => error);
				}
				// Otherwise create a generic message
				return throwError(() => new Error('Failed to upload file. Please try again.'));
			}),
		);
	}

	/**
	 * For a manual TREO spreadsheet import (.xlsx/.csv in the treo environment), returns a
	 * copy of the file whose name carries "__u=<user>__t=<timestamp>" markers so the backend
	 * can reproduce TREO's synthesized suite name. An .xlsx is additionally placed in an
	 * "xlsx/" subfolder of the import folder (the backend converts it to a csv kept under
	 * xlsx/csv/), so the file name is prefixed with "xlsx/". Returns the file unchanged for
	 * any other environment/extension, or when the user name is unavailable.
	 * @param file
	 */
	private tagTreoManualImportFile(file: File): File {
		const environment = this.environmentService.getCurrentEnvironment();
		if (!isTreoEnvironment(environment)) {
			return file;
		}
		const lower = file.name.toLowerCase();
		const isXlsx = lower.endsWith('.xlsx');
		if (!isXlsx && !lower.endsWith('.csv')) {
			return file;
		}

		const marked = this.treoMarkedFileName(file.name);
		if (marked === file.name) {
			return file; // no logged-in user — leave the name unchanged
		}
		// An .xlsx goes under xlsx/ so its converted csv can live under xlsx/csv/; a directly
		// uploaded .csv stays at the import root so the backend still parses it.
		const taggedName = isXlsx ? `xlsx/${marked}` : marked;

		return new File([file], taggedName, { type: file.type, lastModified: file.lastModified });
	}

	/**
	 * Current time as a TREO-style yyyyMMddHHmmss timestamp in the uploader's LOCAL time. The backend
	 * derives the suite's StartTime from this marker and stores it verbatim (no timezone conversion),
	 * because we cannot know which local zone a result was produced in — startTime is always kept and
	 * displayed as-is. Using local getters records the wall-clock time where the upload happened.
	 */
	private treoImportTimestamp(): string {
		const d = new Date();
		const pad = (n: number) => n.toString().padStart(2, '0');
		return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
	}

	/**
	 * Appends the TREO "__u=<user>__t=<timestamp>" markers to a base file name (before its
	 * extension), so the backend can derive the suite's StartTime (local import time) and reproduce
	 * TREO's suite_name. Returns the name unchanged when no logged-in user is available.
	 *
	 * Used both by the direct upload path and by the import dialog when naming the xlsx entry
	 * inside the bundled zip, so a zipped (results + readout) import gets the same StartTime/
	 * suite_name as a direct xlsx upload — not the backend's UTC import-folder fallback.
	 *
	 * When `sop` is given (the SOP name the user picked in the import dialog, e.g. "Main" or
	 * "2610"), a "__s=<sop>" marker is appended so the backend records that SOP instead of its
	 * default. The value is sanitized to marker-safe characters (no "_", which delimits markers).
	 * The SOP marker is emitted even when no user name is available (unlike __u/__t, which need
	 * the user); it just rides on its own.
	 * @param fileName
	 * @param sop
	 */
	public treoMarkedFileName(fileName: string, sop?: string): string {
		let userName = '';
		this.authService.userData$.pipe(take(1)).subscribe(u => {
			userName = u?.userName ?? '';
		});
		userName = userName.replace(/[^A-Za-z0-9]/g, '');
		const sopMarker = sop ? `__s=${sop.replace(/[^A-Za-z0-9]/g, '')}` : '';
		if (!userName && !sopMarker) {
			return fileName;
		}
		const dot = fileName.lastIndexOf('.');
		const stem = dot >= 0 ? fileName.slice(0, dot) : fileName;
		const ext = dot >= 0 ? fileName.slice(dot) : '';
		const userTimeMarkers = userName ? `__u=${userName}__t=${this.treoImportTimestamp()}` : '';
		return `${stem}${userTimeMarkers}${sopMarker}${ext}`;
	}

	/**
	 * Uploads a test result import file with progress tracking
	 * @param file The file to upload
	 * @returns Observable of HTTP events including upload progress
	 */
	public uploadImportFile$(file: File): Observable<HttpEvent<any>> {
		// For manual TREO spreadsheet imports, tag the file name with the logged-in user and an
		// import timestamp so the backend can reproduce TREO's full suite_name
		// (testperiod_config_timestamp_user). Other imports (and Flowlink uploads, which don't
		// go through here) keep their original name and fall back to testperiod_config.
		file = this.tagTreoManualImportFile(file);

		if (this.isDev) {
			console.log('[FileUploadService] Uploading import file:', file.name);
		}

		// Step 1: Get presigned URL from GraphQL
		return from(
			this.apolloClientService.apolloClient$.pipe(
				switchMap(client => {
					const environment = this.environmentService.getCurrentEnvironment();

					const variables = {
						analyzerEnvironment: environment,
						fileName: file.name,
					};

					if (this.isDev) {
						console.log('[FileUploadService] Import query variables:', variables);
					}

					return client.query<PresignedUrlImportResponse>({
						query: GENERATE_PRESIGNED_URL_IMPORT,
						variables: variables,
						fetchPolicy: 'no-cache',
					});
				}),
				// Complete the long-lived apolloClient$ stream on the first result so
				// the timeout below disarms on completion instead of firing ~30s later
				// (see uploadFile).
				take(1),
				// Guard only the presigned-URL request. The S3 upload below keeps its
				// own timeout(120000).
				timeout(60000),
			),
		).pipe(
			switchMap(result => {
				if (this.isDev) {
					console.log('[FileUploadService] Import query result:', result);
				}

				if (result.error) {
					console.error('[FileUploadService] GraphQL error:', result.error);
					throw new Error(`GraphQL error: ${result.error.message}`);
				}

				if (!result.data) {
					console.error('[FileUploadService] No data in result:', result);
					throw new Error('No data returned from presigned URL query');
				}

				const presignedUrl = result.data.analyzerGeneratePutPresignedUrlImport?.url;
				if (!presignedUrl) {
					throw new Error('No presigned URL in response');
				}

				if (this.isDev) {
					console.log('[FileUploadService] Got presigned URL, uploading import file...');
				}

				// Step 2: Upload file to S3 using presigned URL with progress tracking.
				// Derive Content-Type from the filename (see uploadFile) for consistent storage.
				return this.httpClient
					.put(presignedUrl, file, {
						headers: {
							'Content-Type': resolveContentType(file.name, file.type),
						},
						reportProgress: true,
						observe: 'events',
					})
					.pipe(
						timeout(120000), // 2 minute timeout for large files
						catchError((error: any) => {
							console.error('[FileUploadService] S3 import upload error:', error);
							const userMessage = this.formatUploadError(error);
							return throwError(() => new Error(userMessage));
						}),
					);
			}),
			catchError((error: any) => {
				console.error('[FileUploadService] Import upload error:', error);
				if (error.name === 'TimeoutError') {
					return throwError(() => new Error('Request timed out. Please check your connection and try again.'));
				}
				// If error is already formatted with a message, pass it through
				if (error instanceof Error && error.message) {
					return throwError(() => error);
				}
				// Otherwise create a generic message
				return throwError(() => new Error('Failed to upload file. Please try again.'));
			}),
		);
	}
}
